import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions, getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions } from '@onefootprint/fetch-dashboard';
import { useIntl } from '@onefootprint/hooks';
import { IcoMaximize16 } from '@onefootprint/icons';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { Card, LinkButton } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BUSINESS_INSIGHT_SUBSECTIONS, Decrypt, DocumentsDecrypt, EncryptedBusinessInsights, groupRiskSignals, ObeRules, OnboardingBusinessInsight, OnboardingDocuments, OnboardingFieldValidations, OnboardingRiskSignals, OnboardingRules, OnboardingVaultData, type SubsectionType as Subsection, Subsection as SubsectionBody } from '@/components/entity-onboarding-details';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useEntityKind from '@/entity/hooks/use-entity-kind';
import { useOpenPage } from '@/hooks/use-open-page';
import DeviceInsights from '../../../device-insights';
import DuplicateData from '../../../duplicate-data';
import OnboardingMoreInfo from '../../../onboardings-shared/components/onboarding-more-info';
import { CURRENT_ONBOARDING_ID, getSubsectionAnchorId } from '../../../onboardings-shared/constants';
import useOnboardingVault from '../../../onboardings-shared/hooks/use-onboarding-vault';
import useShowOnboarding from '../../../onboardings-shared/hooks/use-show-onboarding';
import OtherInsights from '../../../other-insights';
import useEmptySubsections from './hooks/use-empty-subsections';
import useSubsections from './hooks/use-subsections';
type OnboardingDataProps = WithEntityProps & {
  onboarding: EntityOnboarding;
};
type SubsectionCard = {
  key: string;
  title: string;
  actions?: ReactNode;
  currentLink?: ReactNode;
  /** Content that brings its own border, like a table, sits on the card ground without a body. */
  isBare?: boolean;
  content: ReactNode;
};
const isBusinessInsight = (key: Subsection) => (BUSINESS_INSIGHT_SUBSECTIONS as readonly Subsection[]).includes(key);
const OnboardingData = t0 => {
  const $ = _c(46);
  const {
    entity,
    onboarding
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const entityKind = useEntityKind();
  const handleOpenPage = useOpenPage();
  const {
    scrollOnboardingIntoView
  } = useShowOnboarding();
  let t2;
  if ($[1] !== entity.id || $[2] !== onboarding.id) {
    t2 = getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions({
      path: {
        fpId: entity.id,
        onboardingId: onboarding.id
      }
    });
    $[1] = entity.id;
    $[2] = onboarding.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = Boolean(entity.id) && Boolean(onboarding.id);
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: riskSignals,
    isPending: isRiskSignalsPending
  } = useQuery(t4);
  let t5;
  if ($[7] !== entity.id || $[8] !== onboarding.id) {
    t5 = getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions({
      path: {
        fpId: entity.id,
        onboardingId: onboarding.id
      }
    });
    $[7] = entity.id;
    $[8] = onboarding.id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onboarding.hasGraphTrace || $[11] !== t5) {
    t6 = {
      ...t5,
      enabled: onboarding.hasGraphTrace
    };
    $[10] = onboarding.hasGraphTrace;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    data: onboardingTrace
  } = useQuery(t6);
  const {
    vault: entityVault,
    canDecrypt: hasDecryptableDIs,
    isDecrypted: isVaultDecrypted,
    showDecrypt: showVaultDecrypt
  } = useOnboardingVault(entity, onboarding);
  const subsections = useSubsections(onboarding);
  const isEmpty = useEmptySubsections(entity, onboarding);
  let t7;
  let t8;
  if ($[13] !== entity || $[14] !== entityKind?.urlPath || $[15] !== entityVault || $[16] !== formatDateWithTime || $[17] !== handleOpenPage || $[18] !== hasDecryptableDIs || $[19] !== isEmpty || $[20] !== isRiskSignalsPending || $[21] !== isVaultDecrypted || $[22] !== onboarding || $[23] !== onboardingTrace || $[24] !== riskSignals || $[25] !== scrollOnboardingIntoView || $[26] !== showVaultDecrypt || $[27] !== subsections || $[28] !== t) {
    const asOfText = onboarding.completedAt ? t(_temp, {
      date: formatDateWithTime(new Date(onboarding.completedAt))
    }) : t(_temp2);
    let t9;
    if ($[31] !== scrollOnboardingIntoView || $[32] !== t) {
      t9 = key => <LinkButton onClick={() => scrollOnboardingIntoView(CURRENT_ONBOARDING_ID, key)}>{t(_temp3)}</LinkButton>;
      $[31] = scrollOnboardingIntoView;
      $[32] = t;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    const getCurrentLink = t9;
    let t10;
    if ($[34] !== entity || $[35] !== entityVault.data || $[36] !== entityVault.update || $[37] !== hasDecryptableDIs || $[38] !== onboarding) {
      t10 = <Decrypt canDecrypt={hasDecryptableDIs} onDecryptSuccess={entityVault.update} onboarding={onboarding} vaultData={entityVault.data} entity={entity} />;
      $[34] = entity;
      $[35] = entityVault.data;
      $[36] = entityVault.update;
      $[37] = hasDecryptableDIs;
      $[38] = onboarding;
      $[39] = t10;
    } else {
      t10 = $[39];
    }
    const decryptAction = t10;
    const getCard = (key_0, title) => {
      switch (key_0) {
        case "more-information":
          {
            return {
              key: key_0,
              title,
              actions: onboarding.hasGraphTrace ? <LinkButton iconComponent={IcoMaximize16} iconPosition="left" onClick={event => handleOpenPage(event, `/${entityKind?.urlPath}/${entity.id}/onboardings/${onboarding.id}/trace`)}>{t(_temp4)}</LinkButton> : undefined,
              content: <OnboardingMoreInfo onboarding={onboarding} />
            };
          }
        case "rules":
          {
            if (!onboarding.hasGraphTrace) {
              return {
                key: key_0,
                title,
                content: <OnboardingRules ruleSetResults={onboarding.ruleSetResults} />
              };
            }
            return {
              key: key_0,
              title,
              content: onboardingTrace ? <ObeRules trace={onboardingTrace} /> : null
            };
          }
        case "risk-signals":
          {
            return {
              key: key_0,
              title,
              content: <OnboardingRiskSignals isLoading={isRiskSignalsPending} riskSignals={groupRiskSignals(riskSignals ?? [])} />
            };
          }
        case "user-data":
        case "business-data":
          {
            return {
              key: key_0,
              title,
              actions: showVaultDecrypt ? decryptAction : null,
              content: <OnboardingVaultData entity={entity} vault={entityVault} />
            };
          }
        case "field-validations":
          {
            return {
              key: key_0,
              title,
              content: <SubsectionBody><OnboardingFieldValidations entityId={entity.id} onboardingId={onboarding.id} /></SubsectionBody>
            };
          }
        case "documents":
          {
            return {
              key: key_0,
              title,
              actions: <DocumentsDecrypt entity={entity} onboarding={onboarding} />,
              content: <OnboardingDocuments entity={entity} onboarding={onboarding} />
            };
          }
        case "duplicate-data":
          {
            return {
              key: key_0,
              title,
              actions: asOfText,
              currentLink: onboarding.completedAt ? getCurrentLink(key_0) : undefined,
              content: <DuplicateData timestamp={onboarding.completedAt} />
            };
          }
        case "device-insights":
          {
            return {
              key: key_0,
              title,
              actions: asOfText,
              currentLink: onboarding.completedAt ? getCurrentLink(key_0) : undefined,
              isBare: true,
              content: <DeviceInsights asOf={onboarding.completedAt} />
            };
          }
        case "other-insights":
          {
            return {
              key: key_0,
              title,
              currentLink: getCurrentLink(key_0),
              content: <SubsectionBody><OtherInsights onboardingId={onboarding.id} /></SubsectionBody>
            };
          }
        default:
          {
            return {
              key: key_0,
              title,
              content: <OnboardingBusinessInsight selectedSubsection={key_0} onboarding={onboarding} />
            };
          }
      }
    };
    const cards = subsections.flatMap(t11 => {
      const {
        key: key_1,
        title: title_0
      } = t11;
      if (!isBusinessInsight(key_1) || isVaultDecrypted) {
        return [getCard(key_1, title_0)];
      }
      if (key_1 !== BUSINESS_INSIGHT_SUBSECTIONS[0]) {
        return [];
      }
      return [{
        key: "business-insights",
        title: t(_temp5),
        content: <EncryptedBusinessInsights canDecrypt={hasDecryptableDIs} entity={entity} onboarding={onboarding} onDecryptSuccess={entityVault.update} vaultData={entityVault.data} />
      }];
    });
    t7 = "flex flex-col gap-4";
    let t12;
    if ($[40] !== isEmpty || $[41] !== t) {
      t12 = t13 => {
        const {
          key: key_2,
          title: title_1,
          actions,
          currentLink,
          isBare,
          content
        } = t13;
        const isCardEmpty = isEmpty[key_2 as Subsection];
        const label = isCardEmpty ? t(_temp6) : actions;
        return <Card.Root key={key_2} id={getSubsectionAnchorId(key_2)}><Card.Header title={title_1} rightContent={currentLink ? <div className="flex flex-wrap items-center justify-end gap-x-3 text-body-2 text-tertiary">{label}{currentLink}</div> : label} />{!isCardEmpty && (isBare ? content : <Card.Body className="overflow-hidden">{content}</Card.Body>)}</Card.Root>;
      };
      $[40] = isEmpty;
      $[41] = t;
      $[42] = t12;
    } else {
      t12 = $[42];
    }
    t8 = cards.map(t12);
    $[13] = entity;
    $[14] = entityKind?.urlPath;
    $[15] = entityVault;
    $[16] = formatDateWithTime;
    $[17] = handleOpenPage;
    $[18] = hasDecryptableDIs;
    $[19] = isEmpty;
    $[20] = isRiskSignalsPending;
    $[21] = isVaultDecrypted;
    $[22] = onboarding;
    $[23] = onboardingTrace;
    $[24] = riskSignals;
    $[25] = scrollOnboardingIntoView;
    $[26] = showVaultDecrypt;
    $[27] = subsections;
    $[28] = t;
    $[29] = t7;
    $[30] = t8;
  } else {
    t7 = $[29];
    t8 = $[30];
  }
  let t9;
  if ($[43] !== t7 || $[44] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
};
export default OnboardingData;
function _temp($) {
  return $.asOf;
}
function _temp2($_0) {
  return $_0.current;
}
function _temp3($_1) {
  return $_1.viewCurrent;
}
function _temp4($_2) {
  return $_2.onboardingTrace.showExpandedView;
}
function _temp5($_3) {
  return $_3.businessShared.title;
}
function _temp6($_4) {
  return $_4.none;
}